import { memo } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle, XCircle } from 'lucide-react';
import RichTextBlockEleve from '../../../components/VueQuestion/RichTextBlockEleve';
import CorrectionItem from '../../EspaceEleve/CorrectionItem';
import { getQuestionTypeLabel } from '../../../utils/questionUtils';
import QCMRenderer from '../../../components/VueQuestion/renderers/QCMRenderer';
import RelierRenderer from '../../../components/VueQuestion/renderers/RelierRenderer';
import OrdonnerRenderer from '../../../components/VueQuestion/renderers/OrdonnerRenderer';
import ClasserRenderer from '../../../components/VueQuestion/renderers/ClasserRenderer';
import { getInitials, getScoreColor } from './ResultatsExercice';

// Lettres pour le matching ArUco
const LETTRES = 'ABCDEFGH';

/**
 * Panel modal plein écran affichant toutes les questions d'un élève.
 * Auto-scroll vers la question focusée.
 */
const ResultatsElevePanel = memo(function ResultatsElevePanel({ selectedEleve, focusQuestion, questions, isEnLigne, onClose, onFocusConsumed }) {
  if (!selectedEleve) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="bg-sky-50 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        style={{ animation: 'fadeInUp 0.25s ease-out' }}
      >
        {/* Header sticky — avatar + nom + score */}
        <div className="sticky top-0 z-10 px-5 py-3 bg-slate-50/95 backdrop-blur-sm border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full flex items-center justify-center text-[11px] font-bold text-white flex-shrink-0 border-2 border-sky-600"
              style={{ background: 'linear-gradient(135deg, #3B82F6, #06B6D4)', boxShadow: '0 4px 12px rgba(59,130,246,0.25)' }}>
              {getInitials(selectedEleve.nom)}
            </div>
            <div>
              <span className="text-sm font-bold text-gray-800">{selectedEleve.nom}</span>
              <div className="flex items-center gap-2">
                <span className={`text-xs font-extrabold ${getScoreColor(selectedEleve.score, selectedEleve.total)}`}>
                  {selectedEleve.score}/{selectedEleve.total}
                </span>
                <span className="text-[10px] text-gray-400">
                  {selectedEleve.total > 0 ? Math.round((selectedEleve.score / selectedEleve.total) * 100) : 0}% de réussite
                </span>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition">✕</button>
        </div>

        {/* Liste de toutes les questions */}
        <div className="p-4 space-y-5">
          {questions.map((q) => {
            const rep = selectedEleve.reponses.find(r => r.question === q.question_ordre);
            const isFocus = focusQuestion === q.question_ordre;

            // Matching lettre → contenu pour ArUco
            let reponseAffichee = rep?.reponse || '';
            let bonneReponseAffichee = q.bonneReponse || '';
            if (!isEnLigne && q.choix?.length) {
              const repIdx = LETTRES.indexOf(rep?.reponse);
              const bonIdx = LETTRES.indexOf(q.bonneReponse);
              if (repIdx >= 0 && repIdx < q.choix.length) {
                const c = q.choix[repIdx];
                reponseAffichee = typeof c === 'object' ? JSON.stringify(c) : c;
              }
              if (bonIdx >= 0 && bonIdx < q.choix.length) {
                const c = q.choix[bonIdx];
                bonneReponseAffichee = typeof c === 'object' ? JSON.stringify(c) : c;
              }
            }

            // ItemBilanDetail pour CorrectionItem : la fiche de la copie entière, avec
            // ses réglages de correction (tolérance « +8,9 » = 8,9, trous sans ordre,
            // écritures admises), pour que le détail juge comme le score et comme la
            // correction de l'élève (2026-10-05) ; la réponse et la bonne réponse
            // affichées par-dessus (cartes : la lettre devient le texte du choix).
            const fakeDetail = {
              reponses: {
                _: {
                  [q.question_ordre]: {
                    ...rep?.fiche,
                    réponse: reponseAffichee,
                    bonne_reponse: bonneReponseAffichee,
                    score: rep?.correcte ? 1 : 0,
                  }
                }
              }
            };

            return (
              <div
                key={q.question_ordre}
                ref={el => { if (isFocus && el) { setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100); setTimeout(() => onFocusConsumed?.(), 1500); } }}
                className={`rounded-xl overflow-hidden transition-all duration-300 border shadow-md shadow-slate-800/15 ${
                  isFocus ? 'border-indigo-300 shadow-lg shadow-indigo-500/15 ring-2 ring-indigo-200' : 'border-blue-100/60'
                }`}
                style={{ background: 'linear-gradient(180deg, #ffffff 0%, #f0f7ff 60%, #e8f4f8 100%)' }}
              >
                {/* Header question — neutre, signal subtil via icône */}
                <div className="px-4 py-2.5 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold text-white" style={{ background: '#1E3A8A' }}>Q{q.question_ordre}</span>
                    {rep ? (
                      rep.correcte
                        ? <CheckCircle size={14} className="text-emerald-500" />
                        : <XCircle size={14} className="text-red-500" />
                    ) : (
                      <span className="text-[10px] text-gray-400 italic">Non répondu</span>
                    )}
                    <span className="text-xs text-slate-500">
                      {getQuestionTypeLabel(q)}
                    </span>
                  </div>

                </div>

                {/* Énoncé + renderers — tout aligné px-4 */}
                <div className="px-4 pt-2 pb-1 space-y-1">
                  <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                    Énoncé — {getQuestionTypeLabel(q)}
                  </p>
                  <div className="pl-1 overflow-y-auto text-base font-semibold text-gray-800 [&_img]:max-h-[60px] [&_img]:max-w-full [&_svg]:max-h-[60px] [&_svg]:max-w-full">
                    <RichTextBlockEleve texte={q.texte} elements={q.elements_additionnels} theme="light" />
                  </div>
                  {/* Format question — renderer statique (ce que l'élève a vu) */}
                  {(() => {
                    const c = q.contenu || {};
                    if (q.type === 'qcm' && c.choix?.length) return <QCMRenderer choix={c.choix} bonneReponse="" light />;
                    if (q.subType === 'relier' && c.pairs) return <RelierRenderer pairs={c.pairs} ciblesLeurres={c.cibles_leurres} light />;
                    if (q.subType === 'ordonner' && c.items) return <OrdonnerRenderer items={c.items} light />;
                    if (q.subType === 'classer' && c.categories) return <ClasserRenderer categories={c.categories} light />;
                    return null;
                  })()}
                </div>

                {/* Divider labellé — séparation énoncé / réponse */}
                {rep && (
                  <div className="px-4 py-1">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-px bg-gray-200" />
                      <span className={`text-[10px] font-bold uppercase tracking-wider ${rep.correcte ? 'text-emerald-500' : 'text-red-400'}`}>
                        Réponse de l'élève
                      </span>
                      <div className="flex-1 h-px bg-gray-200" />
                    </div>
                  </div>
                )}

                {/* Correction — flip géré en interne par CorrectionItem */}
                {rep && (
                  <div className="px-4 pb-3 pt-1">
                    <CorrectionItem
                      itemId={q.question_ordre}
                      ItemBilanDetail={fakeDetail}
                      testSpecifique="_"
                      typeQuestion={q.contenu?.multi ? 'qcm_multiple' : q.type}
                      subType={q.subType}
                      suffixe={q.contenu?.suffixe}
                      contenu={q.contenu}
                      allowFlip={!rep.correcte}
                      theme="light"
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
});

export default ResultatsElevePanel;
